/**
 * Экран AI-торговли: выбор пары и ставки → «скан» → серия Smart Scaling → результат.
 * Визуал по design-spec §7 (AI): Signal Setup, analysis-card, direction-card.
 *
 * Серию считает сервер. Важное отличие от прошлой версии: ответ приходит не
 * целиком, а по частям — `POST /api/trade/cycle` возвращает `runId`, а шаги
 * подтягиваются опросом. Иначе в режиме реальных демо-сделок запрос висел бы
 * всю экспирацию (и обрывался бы по таймауту), а на платформе сделки не
 * появлялись бы вовсе.
 */
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { PairSheet } from '../components/PairSheet.js';
import { ApiError, api, apiMessage } from '../lib/api.js';
import type { AppConfig, Asset, ScanIndicatorGroup, SettingsPayload, TradeRun } from '../lib/api.js';
import { haptic } from '../lib/telegram.js';
import { money, payoutPercent } from '../lib/format.js';
import { useApp } from '../state/app.js';

type Phase = 'setup' | 'analyzing' | 'trading' | 'result';

/** Как часто спрашиваем сервер о состоянии серии. */
const POLL_MS = 1000;
/** Потолок паузы между попытками, когда сервер не отвечает (растёт вдвое за неудачу). */
const POLL_MAX_MS = 15_000;
/** Столько неудач подряд — и опрос сдаётся: экран не должен «идти» вечно. */
const POLL_GIVE_UP = 12;
/** Лимит избранных пар — ровно тот, что принимает сервер (`favouritePairs.maxItems`). */
const MAX_FAVOURITES = 12;

/** Тик анимации «скана»: пары и индикаторы обновляются с этим шагом. */
const SCAN_TICK_MS = 320;
/** Сколько тиков «сканируется» каждый этап, прежде чем получить галочку. */
const TICKS_PER_STAGE = 2;

/** Фолбэк на время загрузки конфига — минимальный пул, чтобы скан не был пустым. */
const FALLBACK_INDICATORS: ScanIndicatorGroup[] = [
  {
    label: 'RSI · MACD',
    metrics: [
      { name: 'RSI', min: 34, max: 80, digits: 1 },
      { name: 'MACD', min: -1.2, max: 1.2, digits: 2 },
    ],
  },
  { label: 'MA50 · MA200', metrics: [{ name: 'MA Δ', min: -0.8, max: 0.8, digits: 2, suffix: '%' }] },
];

/** Случайные 2–3 группы из пула без повторов — свой набор на каждый скан. */
function pickIndicators(pool: ScanIndicatorGroup[]): ScanIndicatorGroup[] {
  const source = pool.length > 0 ? [...pool] : [...FALLBACK_INDICATORS];
  const count = Math.min(source.length, Math.random() < 0.5 ? 2 : 3);
  const picked: ScanIndicatorGroup[] = [];
  while (picked.length < count && source.length > 0) {
    const [group] = source.splice(Math.floor(Math.random() * source.length), 1);
    if (group) picked.push(group);
  }
  return picked;
}

/** Значение метрики для тика: x ∈ [0, 1) разворачивается в диапазон метрики. */
function metricText(metric: ScanIndicatorGroup['metrics'][number], x: number): string {
  const value = metric.min + Math.min(1, Math.max(0, x)) * (metric.max - metric.min);
  return `${metric.name} ${value.toFixed(metric.digits)}${metric.suffix ?? ''}`;
}

/**
 * Живой «скан рынка» вместо статичной карточки: этапы проходят по очереди,
 * тикер перебирает НАСТОЯЩИЕ пары брокера с их payout, растёт счётчик
 * проверенных активов, значения индикаторов «дышат». Числа индикаторов —
 * детерминированные функции тика: живые глазу, стабильные между рендерами.
 * Набор индикаторов случайный на каждый запуск скана (пул — с сервера).
 */
function AnalysisCard({
  assets,
  pool,
  t,
}: {
  assets: Asset[];
  pool: ScanIndicatorGroup[];
  t: (key: string, params?: Record<string, string | number>) => string;
}) {
  const [tick, setTick] = useState(0);
  // набор выбирается один раз на маунт карточки = один прогон скана
  const [indicators] = useState(() => pickIndicators(pool));

  useEffect(() => {
    const timer = setInterval(() => setTick((value) => value + 1), SCAN_TICK_MS);
    return () => clearInterval(timer);
  }, []);

  const stages = [
    t('app.trade.scan_quotes'),
    ...indicators.map((group) => group.label),
    t('app.trade.scan_pattern'),
  ];
  const stageIdx = Math.min(Math.floor(tick / TICKS_PER_STAGE), stages.length);
  const total = assets.length > 0 ? assets.length : 120;
  const progress = Math.min(1, tick / (stages.length * TICKS_PER_STAGE + 2));
  const checked = Math.round(progress * total);
  // простые числа перебираются заметнее, чем подряд идущие индексы
  const current = assets.length > 0 ? assets[(tick * 7) % assets.length] : null;

  const metrics = indicators
    .flatMap((group) => group.metrics)
    .slice(0, 3)
    .map((metric, index) => metricText(metric, ((tick + 1) * 0.731 + index * 0.37) % 1));

  return (
    <section className="card card-lg ai-analysis">
      <h2 className="ai-title ai-analysis-title">{t('app.trade.analyzing')}</h2>

      {/* какую пару сканируем прямо сейчас — настоящие тикеры и payout брокера */}
      <div className="scan-ticker">
        <span className="scan-ticker-pair">{current ? (current.label ?? current.symbol) : '···'}</span>
        {current?.payout !== null && current?.payout !== undefined && (
          <span className="scan-ticker-payout">{payoutPercent(current.payout)}%</span>
        )}
      </div>

      <div className="scan-progress">
        <div className="scan-progress-fill" style={{ width: `${Math.round(progress * 100)}%` }} />
      </div>
      <div className="scan-checked">
        {t('app.trade.scan_checked', { done: checked, total })}
      </div>

      <div className="ai-analysis-rows">
        {stages.map((row, index) => (
          <div
            key={row}
            className={`ai-analysis-row ${index < stageIdx ? 'is-done' : index > stageIdx ? 'is-idle' : ''}`}
          >
            <span>{row}</span>
            {index < stageIdx ? (
              <span className="scan-check">✓</span>
            ) : index === stageIdx ? (
              <span className="ai-shimmer" />
            ) : (
              <span className="scan-wait">·</span>
            )}
          </div>
        ))}
      </div>

      <div className="scan-metrics">
        {metrics.map((metric, index) => (
          <span key={index}>{metric}</span>
        ))}
      </div>

      {stageIdx >= stages.length && <p className="step-hint">{t('app.trade.scan_signal')}</p>}
    </section>
  );
}

/** График — тот же ленивый чанк, что у терминала: в основной бандл он не попадает. */
const RunChart = lazy(() => import('./terminal/RunChart.js'));
const CHART_PREF_KEY = 'trade:chart';

function readChartPref(): boolean {
  try {
    return globalThis.localStorage?.getItem(CHART_PREF_KEY) !== 'hidden';
  } catch {
    return true;
  }
}

/** «1:05:09» / «12:40» — сколько осталось работать автопилоту. */
function formatLeft(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1_000));
  const hours = Math.floor(total / 3_600);
  const minutes = Math.floor((total % 3_600) / 60);
  const seconds = String(total % 60).padStart(2, '0');
  return hours > 0 ? `${hours}:${String(minutes).padStart(2, '0')}:${seconds}` : `${minutes}:${seconds}`;
}

const durationLabel = (minutes: number): string => (minutes % 60 === 0 ? `${minutes / 60}h` : `${minutes}m`);

export function TradeScreen({ config }: { config: AppConfig | null }) {
  const { me, patchMe, showToast, setTab, t } = useApp();
  const [assets, setAssets] = useState<Asset[]>([]);
  const [settings, setSettings] = useState<SettingsPayload | null>(null);
  const [pair, setPair] = useState('');
  const [amount, setAmount] = useState(5);
  const [phase, setPhase] = useState<Phase>('setup');
  const [run, setRun] = useState<TradeRun | null>(null);
  const [starting, setStarting] = useState(false);
  const [pairSheetOpen, setPairSheetOpen] = useState(false);
  const [chartOpen, setChartOpen] = useState(readChartPref);
  const [stopping, setStopping] = useState(false);
  const [, setBeat] = useState(0);

  /** Идентификатор серии, за которой следим; null — следить не за чем. */
  const watching = useRef<string | null>(null);

  useEffect(() => {
    void api
      .assets()
      .then(({ assets: list }) => {
        setAssets(list);
        if (list[0]) setPair((current) => current || list[0]!.symbol);
      })
      .catch(() => undefined);
    void api.settings().then(setSettings).catch(() => undefined);
  }, []);

  /** Применяем состояние серии к экрану: шаги, фаза, баланс и токены. */
  const apply = useCallback(
    (next: TradeRun) => {
      // ответ незнакомой формы — почти наверняка старая страница из кэша
      // Telegram против обновлённого API. Рисовать по нему нельзя: получится
      // пустая серия с нулевым результатом (так и вышло 2026-08-20)
      if (!next || typeof next.status !== 'string' || !Array.isArray(next.steps)) {
        setPhase('setup');
        showToast(t('app.trade.outdated'));
        return;
      }

      setRun(next);

      if (next.status === 'running') {
        // «анализ» держим только до первой сделки — дальше показываем шаги
        setPhase(next.steps.length > 0 || next.pending ? 'trading' : 'analyzing');
        return;
      }

      watching.current = null;
      setPhase(next.status === 'failed' && next.steps.length === 0 ? 'setup' : 'result');

      if (next.status === 'failed' && next.steps.length === 0) {
        showToast(t(next.errorKey ?? 'app.trade.failed'));
      }

      if (me) {
        // серия на реальном счёте двигает реальный баланс, а не демо: раньше её итог
        // записывался в `balances.demo`, и на главной демо-баланс «прыгал» до реальных цифр
        patchMe({
          user: { ...me.user, tokens: next.tokensLeft },
          balances:
            next.market === 'real'
              ? { ...me.balances, real: next.balance }
              : { ...me.balances, demo: next.balance },
        });
      }
    },
    [me, patchMe, showToast, t],
  );

  /**
   * Опрос состояния серии — пока она идёт.
   *
   * Ошибки не «глотаются» до бесконечности (разбор 2026-09-10: экран с
   * протухшей подписью дёргал сервер раз в секунду часами — 1 778 × 401 за
   * час): 401/404 останавливают опрос сразу, сетевые сбои — с растущей
   * паузой и потолком попыток.
   */
  const pollFailures = useRef(0);
  useEffect(() => {
    if (!run || run.status !== 'running') {
      pollFailures.current = 0;
      return;
    }
    watching.current = run.runId;

    let cancelled = false;
    const delay = Math.min(POLL_MS * 2 ** pollFailures.current, POLL_MAX_MS);
    const timer = globalThis.setTimeout(() => {
      void api
        .tradeRun(run.runId)
        .then((next) => {
          pollFailures.current = 0;
          if (!cancelled && watching.current === next.runId) apply(next);
        })
        .catch((err: unknown) => {
          if (cancelled) return;
          const status = err instanceof ApiError ? err.status : 0;
          // подпись протухла — дальше все запросы бессмысленны, экран входа
          // покажет AppProvider; серии на сервере нет — она не «идёт»
          const fatal = status === 401 || status === 404;
          pollFailures.current += 1;
          if (fatal || pollFailures.current >= POLL_GIVE_UP) {
            watching.current = null;
            pollFailures.current = 0;
            setRun(null);
            setPhase('setup');
            if (status !== 401) showToast(t(status === 404 ? 'app.trade.failed' : 'app.error.load_failed'));
            return;
          }
          // разрыв связи не должен ронять экран: следующий тик попробует снова
          setRun((current) => (current ? { ...current } : current));
        });
    }, delay);

    return () => {
      cancelled = true;
      globalThis.clearTimeout(timer);
    };
  }, [run, apply, showToast, t]);

  /** Приложение открыли на середине серии — подхватываем её, а не теряем. */
  useEffect(() => {
    void api
      .activeRun()
      .then(({ run: active }) => {
        if (active) apply(active);
      })
      .catch(() => undefined);
    // осознанно один раз при монтировании: дальше состояние ведёт опрос
  }, []);

  async function launch(session = false) {
    if (starting) return;
    haptic('medium');
    setStarting(true);
    setPhase('analyzing');
    setRun(null);

    try {
      const started = await api.runCycle({
        amount,
        ...(pair ? { pair } : {}),
        ...(session ? { session: true } : {}),
      });
      apply(started);
    } catch (err) {
      setPhase('setup');
      if (err instanceof ApiError && err.isOutOfTokens) {
        showToast(t('app.trade.no_tokens'));
        setTab('tokens');
        return;
      }
      // аккаунт не привязан — говорим на языке пользователя и ведём к привязке
      const code =
        err instanceof ApiError ? (err.payload as { error?: string } | null)?.error : null;
      showToast(apiMessage(err, t, 'app.trade.failed'));
      if (code === 'account_required') setTab('account');
    } finally {
      setStarting(false);
    }
  }

  const toggleChart = (): void => {
    haptic();
    setChartOpen((open) => {
      try {
        globalThis.localStorage?.setItem(CHART_PREF_KEY, open ? 'hidden' : 'shown');
      } catch {
        // приватный режим — выбор проживёт до перезапуска
      }
      return !open;
    });
  };

  // обратный отсчёт автопилота тикает сам, без ожидания ответа сервера
  const autopilotRunning = run?.status === 'running' && run.kind === 'autopilot';
  useEffect(() => {
    if (!autopilotRunning) return undefined;
    const timer = globalThis.setInterval(() => setBeat((value) => value + 1), 1_000);
    return () => globalThis.clearInterval(timer);
  }, [autopilotRunning]);
  useEffect(() => {
    if (run?.status !== 'running') setStopping(false);
  }, [run?.status]);

  const stop = (): void => {
    haptic('medium');
    setStopping(true);
    void api.stopRun().catch(() => setStopping(false));
  };

  const autopilotOn = settings?.options.strategies.find((p) => p.id === settings.current.strategy)?.mode === 'auto';
  const autopilot = settings?.current.autopilot ?? { minPercent: 0.5, maxPercent: 2, durationMinutes: 60 };
  const autopilotOptions = settings?.options.autopilot ?? { minPercent: [0.5, 1, 2, 3], maxPercent: [1, 2, 3, 5, 10], durationMinutes: [15, 30, 60, 120, 240] };

  /** Настройка автопилота: экран отвечает сразу, сервер догоняет; не принял — откатываем. */
  const setAutopilot = (patch: Partial<typeof autopilot>): void => {
    haptic();
    const previous = autopilot;
    const next = { ...autopilot, ...patch };
    // коридор не может быть вывернут: двигаем вторую границу следом
    if (next.minPercent > next.maxPercent) {
      if (patch.minPercent !== undefined) next.maxPercent = autopilotOptions.maxPercent.find((value) => value >= next.minPercent) ?? next.minPercent;
      else next.minPercent = [...autopilotOptions.minPercent].reverse().find((value) => value <= next.maxPercent) ?? next.maxPercent;
    }
    const withAutopilot = (value: typeof autopilot) => (current: SettingsPayload | null) =>
      current ? { ...current, current: { ...current.current, autopilot: value } } : current;
    setSettings(withAutopilot(next));
    api.updateSettings({ autopilot: next }).catch(() => {
      setSettings(withAutopilot(previous));
      showToast(t('app.account.save_failed'));
    });
  };

  const bets = settings?.options.bets ?? [
    { amount: 1 },
    { amount: 3 },
    { amount: 5 },
    { amount: 10, risk: true },
    { amount: 20, risk: true },
  ];
  const strategy = settings?.current;
  const favourites = settings?.current.favouritePairs ?? [];

  /** Любимые пары переключаются звёздочкой в шторке; храним на сервере. */
  const toggleFavourite = (symbol: string): void => {
    const starred = favourites.includes(symbol);
    // лимит тот же, что у сервера: раньше 13-я звезда ставилась локально, сервер
    // отвечал 400, и дальше не сохранялось уже ничего (56 отказов за 4 дня, 2026-09-10)
    if (!starred && favourites.length >= MAX_FAVOURITES) {
      showToast(t('app.trade.favourites_limit', { max: MAX_FAVOURITES }));
      return;
    }
    const previous = favourites;
    const next = starred ? favourites.filter((item) => item !== symbol) : [...favourites, symbol];
    const withFavourites = (list: string[]) => (current: SettingsPayload | null) =>
      current ? { ...current, current: { ...current.current, favouritePairs: list } } : current;
    // оптимистично: экран отвечает мгновенно, сервер догоняет; не принял — откатываем,
    // иначе локальный список расходится с сохранённым
    setSettings(withFavourites(next));
    api.updateSettings({ favouritePairs: next }).catch(() => {
      setSettings(withFavourites(previous));
      showToast(t('app.account.save_failed'));
    });
  };

  const allPairs =
    assets.length > 0 ? assets : [{ symbol: 'EUR/USD_otc', label: 'EUR/USD OTC' } as Asset];
  // на сетапе — любимые пары (+ выбранная); пока любимых нет — топ-4 по выплате
  const favouriteAssets = allPairs.filter((asset) => favourites.includes(asset.symbol));
  const shownPairs = [...(favouriteAssets.length > 0 ? favouriteAssets : allPairs.slice(0, 4))];
  if (pair && !shownPairs.some((asset) => asset.symbol === pair)) {
    const selected = allPairs.find((asset) => asset.symbol === pair);
    if (selected) shownPairs.unshift(selected);
  }

  return (
    <div className="screen">
      {phase === 'setup' && (
        <>
          <section className="card card-lg">
            <div className="ai-head">
              <img className="ai-emblem" src="/icons/trade.svg" alt="" width={64} height={64} />
              <div>
                <span className="kicker">Quantum AI</span>
                <h2 className="ai-title">{t(autopilotOn ? 'app.trade.autopilot_title' : 'app.trade.setup_title')}</h2>
              </div>
            </div>

            {autopilotOn && (
              <div className="stack">
                <p className="body">{t('app.trade.autopilot_hint')}</p>
                <div>
                  <div className="label">{t('app.trade.autopilot_min')}</div>
                  <div className="chip-grid" style={{ '--chip-cols': 4 } as React.CSSProperties}>
                    {autopilotOptions.minPercent.map((value) => (
                      <button key={value} type="button" className={`chip ${autopilot.minPercent === value ? 'is-active' : ''}`} onClick={() => setAutopilot({ minPercent: value })}>
                        {value}%
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="label">{t('app.trade.autopilot_max')}</div>
                  <div className="chip-grid" style={{ '--chip-cols': 5 } as React.CSSProperties}>
                    {autopilotOptions.maxPercent.map((value) => (
                      <button key={value} type="button" className={`chip ${autopilot.maxPercent === value ? 'is-active' : ''}`} onClick={() => setAutopilot({ maxPercent: value })}>
                        {value}%
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="label">{t('app.trade.autopilot_duration')}</div>
                  <div className="chip-grid" style={{ '--chip-cols': 5 } as React.CSSProperties}>
                    {autopilotOptions.durationMinutes.map((value) => (
                      <button key={value} type="button" className={`chip ${autopilot.durationMinutes === value ? 'is-active' : ''}`} onClick={() => setAutopilot({ durationMinutes: value })}>
                        {durationLabel(value)}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="ai-strategy-note">
                  {t('app.trade.autopilot_line', {
                    min: `$${money(((me?.user.mode === 'REAL' ? (me?.balances.real ?? 0) : (me?.balances.demo ?? 0)) * autopilot.minPercent) / 100)}`,
                    max: `$${money(((me?.user.mode === 'REAL' ? (me?.balances.real ?? 0) : (me?.balances.demo ?? 0)) * autopilot.maxPercent) / 100)}`,
                    cost: settings?.options.autopilotTradeCost ?? 1,
                  })}
                </div>
              </div>
            )}

            <div className="stack" style={autopilotOn ? { display: 'none' } : undefined}>
              <div>
                <div className="label">{t('app.trade.pair')}</div>
                {/* любимые пары (звёздочка в шторке) + выбранная; остальное — в шторке */}
                <div className="chip-grid">
                  {shownPairs.map((asset) => (
                    <button
                      key={asset.symbol}
                      type="button"
                      className={`chip ${pair === asset.symbol ? 'is-active' : ''}`}
                      onClick={() => {
                        haptic();
                        setPair(asset.symbol);
                      }}
                    >
                      <span className="chip-label">
                        {favourites.includes(asset.symbol) ? '⭐ ' : ''}
                        {asset.label ?? asset.symbol}
                      </span>
                      {asset.payout !== null && asset.payout !== undefined && (
                        <span className="chip-payout">{payoutPercent(asset.payout)}%</span>
                      )}
                    </button>
                  ))}
                  <button
                    type="button"
                    className="chip chip-more"
                    onClick={() => {
                      haptic();
                      setPairSheetOpen(true);
                    }}
                  >
                    <span className="chip-label">{t('app.trade.all_pairs')}</span>
                  </button>
                </div>
              </div>

              <div>
                <div className="label">{t('app.trade.amount')}</div>
                <div className="chip-grid" style={{ '--chip-cols': 3 } as React.CSSProperties}>
                  {bets.map((bet) => (
                    <button
                      key={bet.amount}
                      type="button"
                      className={`chip ${amount === bet.amount ? 'is-active' : ''}`}
                      onClick={() => {
                        haptic();
                        setAmount(bet.amount);
                      }}
                    >
                      ${bet.amount}
                      {bet.risk ? ' RISK' : ''}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {strategy && !autopilotOn && (
              <div className="ai-strategy-note">
                {t('app.trade.strategy_line', {
                  strategy:
                    settings?.options.strategies.find((p) => p.id === strategy.strategy)?.label ??
                    strategy.strategy,
                  progression: strategy.progression,
                  maxSteps: strategy.maxSteps,
                  expiration: strategy.expirationSeconds,
                })}
              </div>
            )}

            <button
              type="button"
              className="cta cta-launch cta-block"
              disabled={starting}
              onClick={() => void launch()}
            >
              {t(autopilotOn ? 'app.trade.autopilot_start' : 'app.trade.generate')}
            </button>

            {/* сессия: maxTrades серий подряд одним запуском (токены — за
                каждую серию); кнопка вторична, чтобы не спорить с главной */}
            {!autopilotOn && (strategy?.maxTrades ?? 0) > 1 && (
              <button
                type="button"
                className="cta cta-secondary cta-block"
                disabled={starting}
                onClick={() => void launch(true)}
              >
                {t('app.trade.session_button', { count: strategy?.maxTrades ?? 0 })}
              </button>
            )}
          </section>

          <section className="card">
            <div className="row-between">
              <span className="label">{t('app.trade.tokens_available')}</span>
              <span className="pill">{me?.user.tokens ?? 0} 💎</span>
            </div>
            <p className="body">{t('app.trade.cycle_note')}</p>
          </section>
        </>
      )}

      {phase === 'analyzing' && (
        <AnalysisCard assets={assets} pool={config?.scanIndicators ?? []} t={t} />
      )}

      {pairSheetOpen && (
        <PairSheet
          assets={allPairs}
          pair={pair}
          favourites={favourites}
          onSelect={(symbol) => {
            setPair(symbol);
            setPairSheetOpen(false);
          }}
          onToggleFavourite={toggleFavourite}
          onClose={() => setPairSheetOpen(false)}
          t={t}
        />
      )}

      {(phase === 'trading' || phase === 'result') && run && (
        <>
          <section className={`card card-lg direction-card ${run.profit >= 0 ? 'is-up' : 'is-down'}`}>
            <span className="kicker">{run.pending?.pairLabel ?? run.chart?.pairLabel ?? run.pairLabel ?? run.pair}</span>
            <div className="value-xl">{(run.pending?.action ?? run.steps.at(-1)?.action ?? run.action) === 'call' ? 'LONG' : 'SHORT'}</div>
            <span className="pill pill-green">{t(run.kind === 'autopilot' ? 'app.trade.autopilot_title' : 'app.trade.smart_scaling')}</span>
            {run.kind === 'autopilot' && run.status === 'running' && run.endsAt && (
              <span className="pill">{t('app.trade.autopilot_left', { time: formatLeft(run.endsAt - Date.now()), count: run.steps.length })}</span>
            )}
            {/* видно, настоящие это сделки или превью — иначе не отличить;
                реальный счёт помечается отдельно: там настоящие деньги */}
            <span className={`pill ${run.market === 'real' ? 'pill-green' : ''}`}>
              {t(
                run.market === 'real'
                  ? 'app.trade.real_badge'
                  : run.live
                    ? 'app.trade.live_badge'
                    : 'app.trade.demo_badge',
              )}
            </span>
          </section>

          {/* окно графика: пара сделки, которая идёт прямо сейчас; сворачивается и запоминает выбор */}
          {run.chart && run.chart.assetId !== null && (
            <section className="card run-chart-card">
              <button type="button" className="run-chart-head" onClick={toggleChart}>
                <span className="label">{run.chart.pairLabel}</span>
                <span className="run-chart-toggle">{t(chartOpen ? 'app.trade.chart_hide' : 'app.trade.chart_show')} {chartOpen ? '▴' : '▾'}</span>
              </button>
              {chartOpen && (
                <Suspense fallback={<div className="run-chart run-chart-loading" />}>
                  <RunChart target={run.chart} pending={run.pending} />
                </Suspense>
              )}
            </section>
          )}

          <section className="card">
            <div className="steps-list">
              {run.steps.map((step) => (
                <div key={step.index} className={`step-row ${step.won ? 'is-win' : 'is-loss'}`}>
                  <span className="step-index">
                    {/* в корзине (basket) каждый шаг на своей паре — она важнее номера */}
                    {step.pairLabel ?? step.pair ?? t('app.trade.step', { index: step.index })}
                  </span>
                  <span className="step-amount">${money(step.amount)}</span>
                  <span className="step-profit">
                    {step.profit > 0 ? '+' : '−'}${money(Math.abs(step.profit))}
                  </span>
                </div>
              ))}

              {/* открытая сделка: у брокера она живёт всю экспирацию, и без
                  этой строки экран выглядел бы зависшим */}
              {run.pending && (
                <div className="step-row is-pending">
                  <span className="step-index">
                    {run.pending.pairLabel ?? t('app.trade.step', { index: run.pending.index })}
                  </span>
                  <span className="step-amount">${money(run.pending.amount)}</span>
                  <span className="step-profit">
                    <span className="ai-shimmer" />
                  </span>
                </div>
              )}
            </div>

            {run.status === 'running' && (
              <p className="step-hint">{t(stopping ? 'app.trade.autopilot_stopping' : 'app.trade.waiting')}</p>
            )}
            {run.status === 'running' && run.kind === 'autopilot' && (
              <button type="button" className="cta cta-secondary cta-block" disabled={stopping} onClick={stop}>
                {t('app.trade.autopilot_stop')}
              </button>
            )}
          </section>
        </>
      )}

      {phase === 'result' && run && (
        <section className="card card-lg victory-card">
          <h2 className="card-title">
            {run.status === 'failed'
              ? t(run.errorKey ?? 'app.trade.failed')
              : t(run.kind === 'autopilot' ? 'app.trade.autopilot_done' : run.won ? 'app.trade.win_title' : 'app.trade.loss_title')}
          </h2>
          {/* автопилот мог закончиться раньше времени — говорим почему (кончились токены, мал баланс) */}
          {run.status !== 'failed' && run.errorKey && <p className="body">{t(run.errorKey)}</p>}
          <div className="victory-grid">
            <div>
              <div className="label">{t('app.trade.result')}</div>
              <div className={`victory-value ${run.profit >= 0 ? 'is-win' : 'is-loss'}`}>
                {run.profit >= 0 ? '+' : '−'}${money(Math.abs(run.profit))}
              </div>
            </div>
            <div>
              <div className="label">{t('app.trade.balance')}</div>
              <div className="victory-value">${money(run.balance)}</div>
            </div>
            <div>
              <div className="label">{t('app.trade.tokens_spent')}</div>
              <div className="victory-value">{run.tokensSpent} 💎</div>
            </div>
          </div>

          <div className="row">
            <button
              type="button"
              className="cta cta-launch grow"
              onClick={() => {
                setPhase('setup');
                setRun(null);
              }}
            >
              {t('app.trade.again')}
            </button>
            <button type="button" className="cta cta-secondary grow" onClick={() => setTab('home')}>
              {t('app.trade.to_menu')}
            </button>
          </div>
        </section>
      )}
    </div>
  );
}
